<script setup lang="ts">
import dayjs from 'dayjs';
import { useField } from 'vee-validate';
import type { IconName } from '~/components/ui/Icon.vue';
import type { AggregationLevel } from '~/types/new/energy';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { parseTime } from '@internationalized/date';
import { useDateRange } from './composables/useDateRange';
import type { Weekdays } from '~/types/graphql/graphql';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useElementBounding } from '@vueuse/core';

dayjs.extend(weekOfYear);
dayjs.extend(localizedFormat);

// Types
export type ModelValueDateRange = {
  start: string;
  end: string;
  aggregationLevel?: AggregationLevel;
};

// Composables
const { locale } = useI18n();
const { aggregationLevelOptions, inputsTypes, shortcuts } = useDateRange();
const { getHeight, getTextSize, getInputColor, getSelectOpenAnimation } = useInput();

// Props
const modelValue = defineModel<ModelValueDateRange>();
const props = withDefaults(
  defineProps<{
    name: string;
    label?: string;
    required?: boolean;
    size?: InputSize;
    leadingIcon?: IconName;
    leadingText?: string;
    trailingIcon?: IconName;
    disabled?: boolean;
    placeholder?: string;
    disabledAggregationLevel?: boolean;
    hideAggregationLevel?: boolean;
    weekStart?: Weekdays['value'];
    content?: {
      direction?: 'left' | 'bottom' | 'right' | 'top';
      align?: 'start' | 'center' | 'end';
      sideOffset?: number;
    };
  }>(),
  {
    label: undefined,
    leadingIcon: undefined,
    leadingText: undefined,
    trailingIcon: 'ChevronDown',
    size: 'md',
    disabled: false,
    placeholder: undefined,
    weekStart: 'monday',
    content: () => ({
      direction: 'bottom',
      align: 'start',
      sideOffset: 4,
    }),
  },
);

// Data
const open = ref(false);
const hourFocused = ref(false);
const selectedDate = ref<keyof Omit<ModelValueDateRange, 'aggregationLevel'> | null>(null);
const input = ref(null);
const { width } = useElementBounding(input);
const { errorMessage, setValue } = useField<ModelValueDateRange>(props.name, undefined, {
  initialValue: modelValue.value ?? undefined,
  valueProp: modelValue.value ?? undefined,
});

// Methods
const selectDate = (date: string | undefined, type: keyof Omit<ModelValueDateRange, 'aggregationLevel'>) => {
  if (!modelValue.value || !date) return;
  modelValue.value = {
    ...modelValue.value,
    [type]: dayjs(date).format('YYYY-MM-DD HH:mm'),
  };
};

const selectShortcut = async (dateRange: { start: Date; end: Date }) => {
  if (!modelValue.value) return;
  modelValue.value = {
    ...modelValue.value,
    start: dayjs(dateRange.start).format('YYYY-MM-DD HH:mm'),
    end: dayjs(dateRange.end).format('YYYY-MM-DD HH:mm'),
  };
};

// Computed
const aggregationLevel = computed({
  get: () => modelValue.value?.aggregationLevel ?? ('day' as AggregationLevel),
  set: (newValue: AggregationLevel) => {
    if (modelValue.value) {
      modelValue.value = {
        ...modelValue.value,
        aggregationLevel: newValue,
      };
    }
  },
});

const formatedDateForDisplay = computed(() => {
  return {
    start: {
      date: dayjs(modelValue.value?.start).format('L'),
      time: dayjs(modelValue.value?.start).format('LT'),
    },
    end: {
      date: dayjs(modelValue.value?.end).format('L'),
      time: dayjs(modelValue.value?.end).format('LT'),
    },
  };
});

// Lifecycle
watch(
  () => modelValue.value,
  (newValue) => {
    if (newValue) setValue(newValue);
  },
);

watch(
  () => open.value,
  () => {
    selectedDate.value = null;
  },
);
</script>
<template>
  <div v-if="modelValue" class="text-gray-600 w-full">
    <popover-root v-model:open="open" class="w-full">
      <label v-if="label" :class="errorMessage ? 'text-red-300' : 'text-gray-600'" class="font-semibold text-xs mb-[6px] block text-left">
        <span v-if="required" class="text-red-500">*</span>
      </label>
      <popover-trigger class="w-full">
        <slot name="trigger" :open="open">
          <div
            ref="input"
            class="px-2.5 border rounded-md flex justify-between items-center w-full bg-white"
            :class="[getHeight(size), getTextSize(size), getInputColor({ errorMessage, disabled, open })]"
          >
            <div class="flex items-center gap-2">
              <ui-icon v-if="leadingIcon" :name="leadingIcon" class="h-4 w-4 stroke-gray-600" />
              <span class="flex-1 text-gray-600 truncate select-none flex items-center">
                <span v-if="leadingText" class="text-gray-600">{{ leadingText }} : </span>
                <span>
                  <!-- ============== Start Date ============== -->
                  <span class="capitalize">
                    {{ formatedDateForDisplay.start.date }}
                  </span>

                  <!-- End Time -->
                  <span class="text-[10px] ml-1 !capitalize">
                    {{ formatedDateForDisplay.start.time }}
                  </span>

                  <!-- ============== End Date ============== -->
                  {{ ' ' + $t('global.date_to').toLocaleLowerCase() }}
                  <span class="capitalize">
                    {{ formatedDateForDisplay.end.date }}
                  </span>

                  <!-- End Time -->
                  <span class="text-[10px] ml-1">
                    {{ formatedDateForDisplay.end.time }}
                  </span>
                </span>
              </span>
            </div>
          </div>
        </slot>
      </popover-trigger>
      <popover-portal>
        <popover-content
          position-strategy="fixed"
          :side="content.direction"
          :align="content.align"
          :side-offset="content.sideOffset"
          :class="[getSelectOpenAnimation()]"
          class="z-50 flex items-start bg-white shadow-md border rounded-md border-gray-200"
        >
          <div class="flex flex-col items-center border-r border-gray-200" :style="{ 'min-width': `${width}px` }">
            <!-- Date range selection -->
            <div class="w-full">
              <div class="bg-gray-50 p-2 rounded-t-md">
                <nrjx-button-group
                  v-if="aggregationLevel && !hideAggregationLevel"
                  v-model="aggregationLevel"
                  :items="aggregationLevelOptions"
                  class="mb-4"
                />

                <!-- Date ranges -->
                <div v-if="formatedDateForDisplay">
                  <div v-for="type in inputsTypes" :key="type" class="flex items-center mb-2 select-none">
                    <p class="mr-2 text-xs font-semibold text-gray-600 capitalize w-5">
                      {{ $t(type === 'start' ? 'global.from_date' : 'global.to_date') }}
                    </p>
                    <div
                      class="px-2.5 border rounded-md flex justify-start items-center w-full bg-white"
                      :class="[getHeight(size), getTextSize(size), getInputColor({ errorMessage, disabled, open: selectedDate === type })]"
                      @click="selectedDate = type"
                    >
                      <span class="capitalize">
                        {{ formatedDateForDisplay[type].date }}
                      </span>
                      <span class="ml-1">
                        {{ $t('global.at') + ' ' + formatedDateForDisplay[type].time }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Shortcuts -->
            <div class="w-full bg-white border-t border-gray-200 p-2">
              <p class="text-xs font-semibold text-gray-600 mb-4">{{ $t('global.shortcuts') }}</p>
              <div class="grid grid-flow-col grid-rows-5 gap-4">
                <div
                  v-for="shortcut in shortcuts({ start: modelValue.start, end: modelValue.end })"
                  :key="shortcut.label"
                  class="flex items-center justify-center text-xs w-full py-1 select-none cursor-pointer rounded-md"
                  :class="[shortcut.selected ? 'bg-emerald-100 text-emerald-800' : ' hover:bg-gray-100 text-gray-600']"
                  @click="selectShortcut(shortcut)"
                >
                  {{ shortcut.label }}
                </div>
              </div>
            </div>
          </div>

          <!-- Modal for date selection -->
          <div v-if="selectedDate" class="h-full">
            <!-- Separator -->
            <div class="border-b border-gray-200 p-1 flex items-center justify-center">
              <p class="text-xs font-semibold text-gray-600">
                {{ selectedDate === 'end' ? $t('global.end_date') : $t('global.start_date') }}
              </p>
            </div>

            <!-- Calendars -->
            <div class="w-[272px] p-4">
              <ui-form-input-date-day-calendar
                :model-value="modelValue[selectedDate] || ''"
                :week-start="props.weekStart"
                :rules="[
                  {
                    type: 'disabled-days-after',
                    value: [aggregationLevel === 'day' ? dayjs().subtract(1, 'day').toDate() : dayjs().toDate()],
                  },
                ]"
                @update:model-value="selectDate($event, selectedDate)"
              />

              <!-- Input for minutes -->
              <time-field-root
                v-slot="{ segments }"
                :hour-cycle="24"
                :locale="locale"
                :model-value="modelValue[selectedDate] ? parseTime(dayjs(modelValue[selectedDate]).format('HH:mm')) : null"
                class="border rounded-md bg-white text-gray-600 flex items-center justify-start px-2 mt-2"
                :class="[getHeight(size), getTextSize(size), getInputColor({ errorMessage, disabled, open: hourFocused })]"
                @update:model-value="
                  selectDate(
                    dayjs(modelValue[selectedDate])
                      .hour($event?.hour ?? 0)
                      .minute($event?.minute ?? 0)
                      .format('YYYY-MM-DD HH:mm'),
                    selectedDate,
                  )
                "
              >
                <template v-for="item in segments" :key="item.part">
                  <time-field-input v-if="item.part === 'literal'" :part="item.part">
                    {{ item.value }}
                  </time-field-input>
                  <time-field-input
                    v-else
                    :part="item.part"
                    class="rounded p-[1px]"
                    @focus="hourFocused = true"
                    @blur="hourFocused = false"
                  >
                    {{ item.value }}
                  </time-field-input>
                </template>
              </time-field-root>
            </div>
          </div>
        </popover-content>
      </popover-portal>
    </popover-root>
  </div>
</template>
